import { Component, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import { CommandService } from "~app/common/services/cqs/command.service";
import { FordelaService } from "~app/common/services/fordela.service";
import { SetOverenskommenKvantitetLevorderCommand } from "~app/features/justera-leveransplan/common/commands/setOverenskommenKvantitetCommands";
import { IForsorjningskalla } from "../../../../common/models/forsorjningskalla";
import { AddForsorjningskallaLevorderCommand } from "../../kundorder/common/add-forsorjningskalla";
import { LeverantorsorderradAggregat } from "../leverantorsorder-details/leverantorsorder-leveransrader/leverantorsorderradAggregat";
import { LeverantorsorderService } from "../services/leverantorsorder.service";

@Component({
    selector: "rask-leveransrader-create",
    templateUrl: "./leveransrader-create.component.html",
    styleUrls: ["./leveransrader-create.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        SelectorComponent,
        InputNumberComponent,
        ButtonComponent,
        TranslatePipe
    ]
})
export class LeveransraderCreateComponent {
    form = new UntypedFormGroup({
        mottagningsplats: new UntypedFormControl(),
        totalKvantitet: new UntypedFormControl()
    });
    kundorderradId: number;
    leverantorsorderrad: LeverantorsorderradAggregat;
    modal = inject(NgbActiveModal);

    private readonly commandService = inject(CommandService);
    private readonly fordelaKvantitetService = inject(FordelaService);
    private readonly leverantorsorderService = inject(LeverantorsorderService);

    create() {
        const mottagningsplats = this.form.get("mottagningsplats").value as IForsorjningskalla;
        const totalKvantitet = this.form.get("totalKvantitet").value;
        const periodStart = this.leverantorsorderrad.periodFrom;
        const periodEnd = this.leverantorsorderrad.periodTom;

        if (!mottagningsplats) {
            this.modal.dismiss();
            return;
        }

        this.fordelaKvantitetService.getFordeladKvantitet(totalKvantitet, periodStart, periodEnd)
            .subscribe(result => {
                this.leverantorsorderService.getKundorderradInfo(mottagningsplats.id).subscribe(forsorjningskalla => {
                    forsorjningskalla.kvantitet = totalKvantitet;
                    const model = this.leverantorsorderrad.addForsorjningskalla(forsorjningskalla, result);
                    if (!model) {
                        this.modal.close(false);
                        return;
                    }

                    this.commandService.addTransactionCommand(new AddForsorjningskallaLevorderCommand(c => {
                        c.kundorderradId = forsorjningskalla.id;
                        c.leverantorsorderradId = this.leverantorsorderrad.orderradId;
                        c.fordeladKvantitet = result;
                    }));

                    if (totalKvantitet) {
                        this.commandService.addTransactionCommand(new SetOverenskommenKvantitetLevorderCommand(c => {
                            c.kundorderradId = forsorjningskalla.id;
                            c.leverantorsorderradId = this.leverantorsorderrad.orderradId;
                            c.kvantitet = totalKvantitet;
                        }));
                    }

                    this.modal.close(true);
                });
            });
    }

    dismiss() {
        this.modal.dismiss();
    }

    forsorjningskallaFormatter = result => result ? result.tooltip : undefined;

    searchForsorjningskalla = (searchStr: string) => {
        const request = {
            input: searchStr,
            mattslag: this.leverantorsorderrad.mattslag,
            periodFrom: this.leverantorsorderrad.periodFrom,
            periodTom: this.leverantorsorderrad.periodTom,
            handelssortimentsgruppId: this.leverantorsorderrad.handelssortimentsgrupp.id
        };

        return this.leverantorsorderService.searchForsorjningskalla(request, this.leverantorsorderrad.hasForsorjningskalla);
    };

    selectedFormatter = result => result ? result.kortNamn : undefined;
}
